import { PairFloatFloat } from '@/api/data-contracts';

export interface CommentSaveContext {
  mediaPlanId?: string;
  orderId?: string;
  locationId?: string;
  taskId?: string;
  attachmentId?: string;
  channel?: string;
  useServicesEndpoints?: boolean;
}

export interface CommentSaveArgs {
  message: string;
  files?: File[];
  commentId?: string;
  parentId?: string;
  edit?: boolean;
  coordinates?: number[];
}

interface SavePayload {
  message: string;
  parentId?: string;
  files?: File[];
  coordinates?: PairFloatFloat;
}

export type CommentSaveAction =
  | {
      type: 'editMediaPlan';
      payload: { commentId: string; message: string; coordinates?: PairFloatFloat };
    }
  | { type: 'editOrder'; payload: { commentId: string; message: string } }
  | { type: 'editTask'; payload: { commentId: string; message: string } }
  | { type: 'saveMediaPlan'; payload: SavePayload }
  | { type: 'saveAttachment'; payload: SavePayload }
  | { type: 'saveTask'; payload: SavePayload }
  | { type: 'saveServicesOrder'; payload: { message: string; parentId?: string; files?: File[] } }
  | { type: 'saveOrder'; payload: { message: string; parentId?: string; files?: File[] } };

/**
 * True when a Quill-delta message carries no content (only whitespace/newline text inserts)
 * and no files are attached — the case where saving should be a no-op.
 */
export const isCommentContentEmpty = (message: string, files?: File[]): boolean => {
  const isNotEmpty = JSON.parse(message).ops.some(
    (op: { insert: string }) =>
      typeof op.insert !== 'string' || op.insert.replaceAll('\n', '').trim() !== '',
  );
  return !isNotEmpty && (!files || files.length === 0);
};

/**
 * Resolves which comment mutation the current scope + args target, and the exact payload for it,
 * mirroring the useComments save dispatcher's priority order. Returns null for an empty comment or
 * a scope that matches no branch.
 */
export const resolveCommentSaveAction = (
  ctx: CommentSaveContext,
  args: CommentSaveArgs,
): CommentSaveAction | null => {
  const { message, files, commentId, parentId, edit, coordinates } = args;
  const { mediaPlanId, orderId, locationId, taskId, attachmentId, channel, useServicesEndpoints } =
    ctx;

  if (isCommentContentEmpty(message, files)) return null;

  const coordinatesPair = coordinates
    ? { first: coordinates[0], second: coordinates[1] }
    : undefined;

  if (edit && commentId && mediaPlanId) {
    return { type: 'editMediaPlan', payload: { commentId, message, coordinates: coordinatesPair } };
  } else if (edit && commentId && orderId && locationId) {
    return { type: 'editOrder', payload: { commentId, message } };
  } else if (edit && commentId && taskId) {
    return { type: 'editTask', payload: { commentId, message } };
  } else if (mediaPlanId) {
    return {
      type: 'saveMediaPlan',
      payload: { message, parentId, files, coordinates: coordinatesPair },
    };
  } else if (taskId && attachmentId) {
    return {
      type: 'saveAttachment',
      payload: { message, parentId, files, coordinates: coordinatesPair },
    };
  } else if (taskId && !attachmentId) {
    return {
      type: 'saveTask',
      payload: { message, parentId, files, coordinates: coordinatesPair },
    };
  } else if (useServicesEndpoints && orderId && locationId) {
    return { type: 'saveServicesOrder', payload: { message, parentId, files } };
  } else if (
    orderId &&
    (locationId ||
      channel === 'team' ||
      channel === 'LOCATION_TEAM' ||
      channel === 'services_advertiser')
  ) {
    return { type: 'saveOrder', payload: { message, parentId, files } };
  }

  return null;
};
